import { useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  FileText,
  Search,
  Trash2,
} from "lucide-react";

import { approveWorker, deleteWorker, listPendingWorkers } from "@/features/ops/api/opsApi";
import { useOpsStore } from "@/features/ops/model/opsStore";
import type { PendingWorker } from "@/features/ops/types";
import {
  DocumentPreviewModal,
  type DocPreviewState,
} from "@/features/ops/ui/components/DocumentPreviewModal";
import {
  NEAR_EXPIRY_DAYS,
  certTone,
  permitDaysRemaining,
} from "@/features/ops/ui/components/workerReview";
import {
  PermitExpiryBadge,
  SinBadge,
} from "@/features/ops/ui/components/workerReviewBits";
import { ConfirmDialog } from "@/shared/ui";
import {
  EXPERIENCE_LEVEL_LABELS,
  LEGAL_STATUS_LABELS,
  type ExperienceLevel,
  type LegalStatus,
} from "@/features/workers/types";

interface FlaggedDialogState {
  worker: PendingWorker;
  reason: string;
}

function appliedAgo(iso: string): string {
  const created = new Date(iso).getTime();
  const days = Math.floor((Date.now() - created) / 86_400_000);
  if (days <= 0) return "Applied today";
  if (days === 1) return "Applied yesterday";
  if (days < 30) return `Applied ${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? "Applied 1 month ago" : `Applied ${months} months ago`;
}

type TriState = "any" | "yes" | "no";

interface PendingFilters {
  search: string;
  legalStatus: "any" | LegalStatus | "missing";
  experienceLevel: "any" | ExperienceLevel | "missing";
  idDocument: TriState;
  sin: TriState;
  photo: TriState;
  certifications: TriState;
}

const DEFAULT_FILTERS: PendingFilters = {
  search: "",
  legalStatus: "any",
  experienceLevel: "any",
  idDocument: "any",
  sin: "any",
  photo: "any",
  certifications: "any",
};

function matchesTriState(state: TriState, has: boolean): boolean {
  return state === "any" || (state === "yes" ? has : !has);
}

function applyPendingFilters(workers: PendingWorker[], f: PendingFilters): PendingWorker[] {
  const search = f.search.trim().toLowerCase();
  return workers.filter((w) => {
    if (search) {
      const haystack = `${w.name} ${w.first_name} ${w.last_name} ${w.email} ${w.city ?? ""}`.toLowerCase();
      if (!haystack.includes(search)) return false;
    }
    if (f.legalStatus !== "any") {
      if (f.legalStatus === "missing" ? w.legal_status !== null : w.legal_status !== f.legalStatus) {
        return false;
      }
    }
    if (f.experienceLevel !== "any") {
      if (
        f.experienceLevel === "missing"
          ? w.experience_level !== null
          : w.experience_level !== f.experienceLevel
      ) {
        return false;
      }
    }
    if (!matchesTriState(f.idDocument, w.eligibility_document_url !== null)) return false;
    if (!matchesTriState(f.sin, w.sin_provided)) return false;
    if (!matchesTriState(f.photo, w.photo_url !== null)) return false;
    if (!matchesTriState(f.certifications, w.certifications.length > 0)) return false;
    return true;
  });
}

function isFilterActive(f: PendingFilters): boolean {
  return (
    f.search.trim() !== "" ||
    f.legalStatus !== "any" ||
    f.experienceLevel !== "any" ||
    f.idDocument !== "any" ||
    f.sin !== "any" ||
    f.photo !== "any" ||
    f.certifications !== "any"
  );
}

function FilterBar({
  filters,
  onChange,
  onClear,
  totalCount,
  filteredCount,
}: {
  filters: PendingFilters;
  onChange: (f: PendingFilters) => void;
  onClear: () => void;
  totalCount: number;
  filteredCount: number;
}) {
  const set = <K extends keyof PendingFilters>(key: K, value: PendingFilters[K]) =>
    onChange({ ...filters, [key]: value });
  const active = isFilterActive(filters);

  // Shared input/select classes — keep one source of truth so dark mode + tokens stay consistent.
  const fieldCls =
    "rounded-md border border-border bg-surface px-2 py-1.5 text-xs text-copy focus:outline-none focus:ring-2 focus:ring-ring";

  return (
    <div className="rounded-lg border border-border bg-surface p-3 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-copy-muted" />
          <input
            type="search"
            value={filters.search}
            onChange={(e) => set("search", e.target.value)}
            placeholder="Search name, email, or city"
            className={`${fieldCls} w-full pl-8`}
          />
        </div>

        <select
          value={filters.legalStatus}
          onChange={(e) => set("legalStatus", e.target.value as PendingFilters["legalStatus"])}
          className={fieldCls}
          aria-label="Legal status"
        >
          <option value="any">Legal: any</option>
          <option value="CITIZEN">{LEGAL_STATUS_LABELS.CITIZEN}</option>
          <option value="PERMANENT_RESIDENT">{LEGAL_STATUS_LABELS.PERMANENT_RESIDENT}</option>
          <option value="OPEN_WORK_PERMIT">{LEGAL_STATUS_LABELS.OPEN_WORK_PERMIT}</option>
          <option value="missing">Legal: missing</option>
        </select>

        <select
          value={filters.experienceLevel}
          onChange={(e) =>
            set("experienceLevel", e.target.value as PendingFilters["experienceLevel"])
          }
          className={fieldCls}
          aria-label="Experience level"
        >
          <option value="any">Experience: any</option>
          <option value="ENTRY">{EXPERIENCE_LEVEL_LABELS.ENTRY}</option>
          <option value="INTERMEDIATE">{EXPERIENCE_LEVEL_LABELS.INTERMEDIATE}</option>
          <option value="EXPERIENCED">{EXPERIENCE_LEVEL_LABELS.EXPERIENCED}</option>
          <option value="EXPERT">{EXPERIENCE_LEVEL_LABELS.EXPERT}</option>
          <option value="missing">Experience: missing</option>
        </select>

        <select
          value={filters.idDocument}
          onChange={(e) => set("idDocument", e.target.value as TriState)}
          className={fieldCls}
          aria-label="ID document"
        >
          <option value="any">ID doc: any</option>
          <option value="yes">ID doc: uploaded</option>
          <option value="no">ID doc: missing</option>
        </select>

        <select
          value={filters.sin}
          onChange={(e) => set("sin", e.target.value as TriState)}
          className={fieldCls}
          aria-label="SIN"
        >
          <option value="any">SIN: any</option>
          <option value="yes">SIN: provided</option>
          <option value="no">SIN: missing</option>
        </select>

        <select
          value={filters.photo}
          onChange={(e) => set("photo", e.target.value as TriState)}
          className={fieldCls}
          aria-label="Photo"
        >
          <option value="any">Photo: any</option>
          <option value="yes">Photo: uploaded</option>
          <option value="no">Photo: missing</option>
        </select>

        <select
          value={filters.certifications}
          onChange={(e) => set("certifications", e.target.value as TriState)}
          className={fieldCls}
          aria-label="Certifications"
        >
          <option value="any">Certs: any</option>
          <option value="yes">Certs: ≥1 uploaded</option>
          <option value="no">Certs: none</option>
        </select>
      </div>

      <div className="mt-2 flex items-center justify-between text-xs text-copy-muted">
        <span>
          Showing <span className="font-medium text-copy">{filteredCount}</span> of {totalCount}
          {active ? " (filtered)" : ""}
        </span>
        {active && (
          <button
            type="button"
            onClick={onClear}
            className="rounded-md border border-border bg-surface px-2 py-1 text-xs hover:bg-surface-sunken"
          >
            Clear filters
          </button>
        )}
      </div>
    </div>
  );
}

export function WorkerApprovalQueue() {
  const token = useOpsStore((s) => s.token)!;
  const qc = useQueryClient();
  const [flaggedDialog, setFlaggedDialog] = useState<FlaggedDialogState | null>(null);
  const [docPreview, setDocPreview] = useState<DocPreviewState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<PendingWorker | null>(null);
  // Per-row mutation tracking — keeps loading state scoped to the clicked row
  // instead of spreading across every worker in the queue.
  const [pendingApproveIds, setPendingApproveIds] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<PendingFilters>(DEFAULT_FILTERS);

  const { data, isLoading, error } = useQuery({
    queryKey: ["ops", "pending-workers"],
    queryFn: () => listPendingWorkers(token),
    // New worker submissions arrive from other sessions — poll so the queue
    // stays current without a manual refresh.
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  const filtered = useMemo(
    () => (data ? applyPendingFilters(data, filters) : []),
    [data, filters],
  );

  const approve = useMutation({
    mutationFn: ({
      workerId,
      acknowledgeFlagged,
    }: {
      workerId: string;
      acknowledgeFlagged?: boolean;
    }) => approveWorker(token, workerId, { acknowledgeFlagged }),
    onSuccess: () => {
      // Worker moves out of the pending bucket and into the approved set —
      // every actor that displays worker status needs a refetch.
      qc.invalidateQueries({ queryKey: ["ops", "pending-workers"] });
      qc.invalidateQueries({ queryKey: ["ops", "workers"] });
      qc.invalidateQueries({ queryKey: ["ops", "worker-detail"] });
      qc.invalidateQueries({ queryKey: ["applicants"] });
      qc.invalidateQueries({ queryKey: ["favourites", "mine"] });
      qc.invalidateQueries({ queryKey: ["worker", "me"] });
    },
  });

  const remove = useMutation({
    mutationFn: (workerId: string) => deleteWorker(token, workerId),
    onSuccess: () => {
      setConfirmDelete(null);
      qc.invalidateQueries({ queryKey: ["ops", "pending-workers"] });
      qc.invalidateQueries({ queryKey: ["ops", "workers"] });
      qc.invalidateQueries({ queryKey: ["ops", "upcoming-shifts"] });
      qc.invalidateQueries({ queryKey: ["ops", "payouts"] });
      qc.invalidateQueries({ queryKey: ["applicants"] });
      qc.invalidateQueries({ queryKey: ["favourites", "mine"] });
    },
  });

  const trackPending = (
    setter: Dispatch<SetStateAction<Set<string>>>,
    id: string,
  ) => {
    setter((s) => new Set(s).add(id));
    return {
      onSettled: () =>
        setter((s) => {
          const next = new Set(s);
          next.delete(id);
          return next;
        }),
    };
  };

  if (isLoading) return <div className="text-sm text-muted-foreground">Loading...</div>;
  if (error) return <div className="text-sm text-destructive">Could not load queue.</div>;

  const handleApprove = (w: PendingWorker) => {
    // Near-expiry permits get a confirmation gate so we never silently
    // approve someone whose right to work is about to lapse.
    const permitDays = permitDaysRemaining(w.work_permit_expires_on);
    if (permitDays !== null && permitDays <= NEAR_EXPIRY_DAYS) {
      setFlaggedDialog({
        worker: w,
        reason:
          permitDays < 0
            ? `Work permit expired on ${w.work_permit_expires_on}.`
            : `Work permit expires in ${permitDays} day${permitDays === 1 ? "" : "s"} (${w.work_permit_expires_on}).`,
      });
      return;
    }
    approve.mutate({ workerId: w.id }, trackPending(setPendingApproveIds, w.id));
  };

  const confirmApproveAnyway = () => {
    if (!flaggedDialog) return;
    const workerId = flaggedDialog.worker.id;
    const tracker = trackPending(setPendingApproveIds, workerId);
    approve.mutate(
      { workerId, acknowledgeFlagged: true },
      {
        onSuccess: () => setFlaggedDialog(null),
        onSettled: tracker.onSettled,
      },
    );
  };

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold">Pending approvals</h2>
        <p className="text-xs text-copy-muted">
          Review the worker's ID document, SIN, and certs before approving.
          Near-expiry work permits need an explicit override.
        </p>
      </div>
      {data && data.length > 0 && (
        <FilterBar
          filters={filters}
          onChange={setFilters}
          onClear={() => setFilters(DEFAULT_FILTERS)}
          totalCount={data.length}
          filteredCount={filtered.length}
        />
      )}
      {data && data.length === 0 && (
        <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
          Nobody awaiting approval right now.
        </div>
      )}
      {data && data.length > 0 && filtered.length === 0 && (
        <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
          No workers match these filters.{" "}
          <button
            type="button"
            onClick={() => setFilters(DEFAULT_FILTERS)}
            className="text-primary hover:underline"
          >
            Clear filters
          </button>
        </div>
      )}
      {filtered.map((w) => (
        <div key={w.id} className="rounded-lg border bg-card p-4 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="h-16 w-16 shrink-0 overflow-hidden rounded-full border bg-muted">
              {w.photo_url ? (
                <img src={w.photo_url} alt={w.name} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
                  No photo
                </div>
              )}
            </div>
            <div className="flex-1 space-y-3">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <p className="font-semibold text-base">
                  {w.first_name || w.last_name
                    ? `${w.first_name} ${w.last_name}`.trim()
                    : w.name || "(name pending)"}
                </p>
                {!w.last_name && (
                  <span
                    className="inline-flex items-center rounded-full border border-warning bg-warning-light px-2 py-0.5 text-xs font-medium text-warning-foreground"
                    title="Worker only supplied a single-word name — chase them for a last name."
                  >
                    Last name missing
                  </span>
                )}
                <span className="text-xs text-copy-muted">{appliedAgo(w.created_at)}</span>
              </div>

              <div className="flex flex-wrap gap-2">
                {w.legal_status ? (
                  <span className="inline-flex items-center rounded-full border border-primary bg-primary-light px-2 py-0.5 text-xs font-medium text-primary">
                    {LEGAL_STATUS_LABELS[w.legal_status]}
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full border border-warning bg-warning-light px-2 py-0.5 text-xs font-medium text-warning-foreground">
                    Legal status missing
                  </span>
                )}
                {w.experience_level && (
                  <span className="inline-flex items-center rounded-full border border-border bg-surface-sunken px-2 py-0.5 text-xs font-medium text-copy">
                    {EXPERIENCE_LEVEL_LABELS[w.experience_level]}
                  </span>
                )}
                <PermitExpiryBadge expiresOn={w.work_permit_expires_on} />
                <SinBadge provided={w.sin_provided} />
              </div>

              <dl className="grid grid-cols-1 gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
                <div className="flex gap-2">
                  <dt className="text-copy-muted">First name</dt>
                  <dd className="text-copy">{w.first_name || "—"}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-copy-muted">Last name</dt>
                  <dd className="text-copy">{w.last_name || "—"}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-copy-muted">Email</dt>
                  <dd className="text-copy break-all">{w.email}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-copy-muted">Phone</dt>
                  <dd className="text-copy">{w.phone || "Not provided"}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-copy-muted">City</dt>
                  <dd className="text-copy">{w.city || "Not set"}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-copy-muted">Min hourly rate</dt>
                  <dd className="text-copy">
                    {w.min_hourly_rate ? `$${w.min_hourly_rate}/h` : "No floor set"}
                  </dd>
                </div>
                {w.shift_count > 0 && (
                  <div className="flex gap-2">
                    <dt className="text-copy-muted">Past shifts</dt>
                    <dd className="text-copy">
                      {w.shift_count}
                      {w.rating_avg != null && (
                        <span className="ml-1 text-copy-muted">
                          · ★ {w.rating_avg.toFixed(1)}
                        </span>
                      )}
                    </dd>
                  </div>
                )}
                {w.approved_at && (
                  <div className="flex gap-2">
                    <dt className="text-copy-muted">Previously approved</dt>
                    <dd className="text-copy">
                      {new Date(w.approved_at).toLocaleDateString()}
                    </dd>
                  </div>
                )}
              </dl>

              {w.bio && (
                <p className="rounded-md border border-border bg-surface-sunken px-3 py-2 text-xs text-copy">
                  {w.bio}
                </p>
              )}

              <div>
                <p className="mb-1 text-xs font-medium text-copy-muted">
                  ID document
                </p>
                {w.eligibility_document_url ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setDocPreview({
                          url: w.eligibility_document_url!,
                          workerName:
                            `${w.first_name} ${w.last_name}`.trim() ||
                            w.name ||
                            w.email,
                        })
                      }
                      className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2 py-1 text-xs text-primary hover:bg-surface-sunken"
                    >
                      <FileText className="h-3.5 w-3.5" />
                      View uploaded proof
                    </button>
                    <a
                      href={w.eligibility_document_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-xs text-copy-muted hover:bg-surface-sunken"
                    >
                      <ExternalLink className="h-3 w-3" /> Open in new tab
                    </a>
                    {w.eligibility_document_uploaded_at && (
                      <span className="text-xs text-copy-subtle">
                        Uploaded{" "}
                        {new Date(w.eligibility_document_uploaded_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-copy-subtle">Not uploaded</p>
                )}
              </div>

              <div>
                <p className="mb-1 text-xs font-medium text-copy-muted">Certifications</p>
                {w.certifications.length === 0 ? (
                  <p className="text-xs text-copy-subtle">None uploaded</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {w.certifications.map((cert) => {
                      const tone = certTone(cert);
                      return (
                        <a
                          key={cert.id}
                          href={cert.url}
                          target="_blank"
                          rel="noreferrer"
                          className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs hover:underline ${tone.classes}`}
                        >
                          <span className="font-medium">{cert.category ?? cert.name}</span>
                          <span className="opacity-80">· {tone.label}</span>
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className="flex shrink-0 flex-col gap-2">
              <button
                onClick={() => handleApprove(w)}
                disabled={pendingApproveIds.has(w.id)}
                className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
              >
                Approve
              </button>
              <button
                onClick={() => setConfirmDelete(w)}
                className="inline-flex items-center justify-center gap-1.5 rounded-md border border-border-strong px-3 py-1.5 text-sm font-medium text-destructive-foreground hover:bg-destructive-light disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Reject
              </button>
            </div>
          </div>
        </div>
      ))}

      {docPreview && (
        <DocumentPreviewModal
          state={docPreview}
          onClose={() => setDocPreview(null)}
        />
      )}

      <ConfirmDialog
        open={confirmDelete !== null}
        destructive
        title="Reject and delete this applicant?"
        description={
          <>
            This permanently deletes{" "}
            <span className="font-medium text-copy">
              {confirmDelete?.name || confirmDelete?.email}
            </span>{" "}
            and all of their data. Use this to clear out test or junk
            applicants. This cannot be undone.
          </>
        }
        confirmLabel="Delete permanently"
        loadingLabel="Deleting…"
        loading={remove.isPending}
        onConfirm={() => confirmDelete && remove.mutate(confirmDelete.id)}
        onCancel={() => setConfirmDelete(null)}
      />

      {flaggedDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-4">
          <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-lg">
            <div className="mb-2 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <h3 className="text-lg font-semibold">Approve anyway?</h3>
            </div>
            <p className="text-sm text-copy-muted">
              {flaggedDialog.reason} You're approving{" "}
              <span className="font-medium text-copy">
                {flaggedDialog.worker.name || flaggedDialog.worker.email}
              </span>{" "}
              despite this. Confirm the worker's documents are still valid before
              continuing.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setFlaggedDialog(null)}
                disabled={pendingApproveIds.has(flaggedDialog.worker.id)}
                className="rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-accent disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmApproveAnyway}
                disabled={pendingApproveIds.has(flaggedDialog.worker.id)}
                className="inline-flex items-center gap-1 rounded-md bg-destructive px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-destructive-hover disabled:opacity-50"
              >
                <CheckCircle2 className="h-4 w-4" />
                {pendingApproveIds.has(flaggedDialog.worker.id) ? "Approving…" : "Approve anyway"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
